import { useAppContext } from "../hooks/context"
import { useEffect, useRef } from "react"
import Api from "../api/api"
import { ErrorToast } from "../utils/toast"
import { LoadingStatus } from "../api/loading"
import { writeCacheTimestamps } from "../pages/map/workers/cacheTimestamps"

const isUserRequiredScreen = noUserRequiredPaths => {
  const paths = noUserRequiredPaths.filter(p => window.location.pathname.startsWith(p))
  return paths.length === 0
}

export function Landing({ noUserRequiredPaths }) {
  const { hasData, setData, addLoadingId, removeLoadingId, setLandingViewLoadingState } =
    useAppContext()

  // the paths are static vars so we can just chuck this into a ref
  const noUserRequiredPathsRef = useRef(noUserRequiredPaths)

  const addLoadingIdRef = useRef(addLoadingId)
  const removeLoadingIdRef = useRef(removeLoadingId)
  const setDataRef = useRef(setData)
  const setLandingViewLoadingStateRef = useRef(setLandingViewLoadingState)

  useEffect(() => {
    if (!isUserRequiredScreen(noUserRequiredPathsRef.current) || hasData) {
      return
    }

    addLoadingIdRef.current("landing")
    setLandingViewLoadingStateRef.current(LoadingStatus.FETCHING)

    const controller = new AbortController()
    Api.get(`/landing`, { signal: controller.signal })
      .then(async (response) => {
        // Write GeoAPI timestamps BEFORE setData to prevent race condition
        // Map view worker fetch depends on these timestamps being available
        if (response.data.geoapi_metadata) {
          try {
            await writeCacheTimestamps(response.data.geoapi_metadata)
          } catch (e) {
            console.error('Failed to write GeoAPI cache timestamps:', e)
            // Don't fail landing if timestamp write fails
          }
        }
        return response.data
      })
      .then((data) => {
        // Redirect to error page if user has no accessible schools
        if (!data.schools || data.schools.length === 0) {
          const params = new URLSearchParams({
            title: "No Schools Available",
            message: "You do not have access to any schools. Please contact your administrator."
          })
          window.location = `/error?${params.toString()}`
          return
        }
        setDataRef.current(data)
        setLandingViewLoadingStateRef.current(LoadingStatus.FETCHED)
      })
      .catch(e => {
        if (e.handled) return
        console.error(e.error)
        ErrorToast("Unable to load data")
        setLandingViewLoadingStateRef.current(LoadingStatus.ERROR)
      })
      .finally(() => {
        removeLoadingIdRef.current("landing")
      })

    return () => controller.abort()
  }, [hasData])

  return null
}
